import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  HostBinding,
  input,
  signal
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import {
  AutoscalingProfileOverrideDataType,
  AutoscalingProfileOverridesValue
} from '@zerops/models/service-stack';
import {
  BYTE_UNITS,
  ByteUnit,
  NS_UNITS,
  NsUnit,
  decomposeByte,
  decomposeNs,
  encodeByte,
  encodeNs,
  shallowEqualRecords
} from './profile-overrides-form.utils';

export type ProfileOverridesSchemaEntry = {
  name: string;
  dataType: AutoscalingProfileOverrideDataType;
  description: string;
};

export type ProfileOverridesSchema = Record<string, ProfileOverridesSchemaEntry>;

const SEARCH_THRESHOLD = 10;

@Component({
  selector: 'zui-profile-overrides-form',
  templateUrl: './profile-overrides-form.component.html',
  styleUrls: [ './profile-overrides-form.component.scss' ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProfileOverridesFormComponent),
      multi: true
    }
  ]
})
export class ProfileOverridesFormComponent implements ControlValueAccessor {
  @HostBinding('class.is-empty') get emptyClass() { return !this.hasAnyKeys(); }

  // # Inputs
  schema = input<ProfileOverridesSchema | null>({});

  // # Internal state
  #value = signal<AutoscalingProfileOverridesValue>({});
  #displayByKey = signal<Record<string, string>>({});
  #unitByKey = signal<Record<string, ByteUnit | NsUnit>>({});
  search = signal<string>('');

  // # Computed
  schemaEntries = computed(() => {
    const schema = this.schema() ?? {};
    return Object.values(schema).sort((a, b) => a.name.localeCompare(b.name));
  });

  hasAnyKeys = computed(() => this.schemaEntries().length > 0);
  totalCount = computed(() => this.schemaEntries().length);
  setCount = computed(() => Object.keys(this.#value() ?? {}).length);
  showSearch = computed(() => this.totalCount() > SEARCH_THRESHOLD);

  filteredEntries = computed(() => {
    const q = this.search().trim().toLowerCase();
    const entries = this.schemaEntries();
    if (!q) return entries;
    return entries.filter(
      (e) => e.name.toLowerCase().includes(q) || (e.description ?? '').toLowerCase().includes(q)
    );
  });

  byteUnits = BYTE_UNITS;
  nsUnits = NS_UNITS;

  // # CVA
  #propagateChange: (v: AutoscalingProfileOverridesValue) => void = () => { /* CVA */ };
  #propagateTouched: () => void = () => { /* CVA */ };

  writeValue(v: AutoscalingProfileOverridesValue | null) {
    const incoming = v ?? {};
    // Round-trip skip: when the parent echoes back the same value we just propagated,
    // don't reset internal display/unit state — that would clobber user-picked units
    // for keys that have no value yet.
    if (shallowEqualRecords(incoming, this.#value())) return;

    this.#value.set({ ...incoming });

    // Preserve existing local state (unit selections + partial typing) and overlay
    // values from the incoming object.
    const schema = this.schema() ?? {};
    const display: Record<string, string> = { ...this.#displayByKey() };
    const units: Record<string, ByteUnit | NsUnit> = { ...this.#unitByKey() };

    Object.entries(incoming).forEach(([ key, raw ]) => {
      const meta = schema[key];
      if (!meta) {
        display[key] = String(raw ?? '');
        return;
      }
      if (meta.dataType === 'byte' && typeof raw === 'number') {
        const { value, unit } = decomposeByte(raw);
        display[key] = String(value);
        units[key] = unit;
      } else if (meta.dataType === 'nanosecond' && typeof raw === 'number') {
        const { value, unit } = decomposeNs(raw);
        display[key] = String(value);
        units[key] = unit;
      } else if (meta.dataType !== 'bool') {
        // bools are read straight from #value via boolStateOf — no display entry needed.
        display[key] = String(raw ?? '');
      }
    });

    this.#displayByKey.set(display);
    this.#unitByKey.set(units);
  }

  registerOnChange(fn: (v: AutoscalingProfileOverridesValue) => void) { this.#propagateChange = fn; }
  registerOnTouched(fn: () => void) { this.#propagateTouched = fn; }

  // # Template helpers
  displayOf(key: string): string {
    return this.#displayByKey()[key] ?? '';
  }

  unitOf(key: string, dataType: AutoscalingProfileOverrideDataType): ByteUnit | NsUnit | undefined {
    const stored = this.#unitByKey()[key];
    if (stored) return stored;
    if (dataType === 'byte') return 'MiB';
    if (dataType === 'nanosecond') return 'ms';
    return undefined;
  }

  boolStateOf(key: string): 'unset' | 'on' | 'off' {
    const raw = this.#value()[key];
    if (raw === true) return 'on';
    if (raw === false) return 'off';
    return 'unset';
  }

  // # Mutations
  onTextChange(key: string, dataType: AutoscalingProfileOverrideDataType, displayed: string) {
    this.#displayByKey.update((m) => ({ ...m, [key]: displayed }));
    this.#recomputeAndCommit(key, dataType);
  }

  onUnitChange(key: string, dataType: 'byte' | 'nanosecond', unit: ByteUnit | NsUnit) {
    this.#unitByKey.update((m) => ({ ...m, [key]: unit }));
    this.#recomputeAndCommit(key, dataType);
  }

  onBoolChange(key: string, state: 'unset' | 'on' | 'off') {
    const next = { ...this.#value() };
    if (state === 'unset') delete next[key];
    else next[key] = state === 'on';
    this.#commit(next);
  }

  resetAll() {
    this.#displayByKey.set({});
    this.#unitByKey.set({});
    this.#commit({});
  }

  // # Private
  #recomputeAndCommit(key: string, dataType: AutoscalingProfileOverrideDataType) {
    const display = this.#displayByKey()[key];
    const next = { ...this.#value() };

    if (display === undefined || display === null || String(display).trim() === '') {
      delete next[key];
      this.#commit(next);
      return;
    }

    if (dataType === 'int' || dataType === 'float') {
      const n = Number(display);
      if (Number.isFinite(n)) next[key] = n; else delete next[key];
    } else if (dataType === 'byte') {
      const n = Number(display);
      const unit = (this.unitOf(key, 'byte') as ByteUnit);
      if (Number.isFinite(n)) next[key] = encodeByte(n, unit); else delete next[key];
    } else if (dataType === 'nanosecond') {
      const n = Number(display);
      const unit = (this.unitOf(key, 'nanosecond') as NsUnit);
      if (Number.isFinite(n)) next[key] = encodeNs(n, unit); else delete next[key];
    } else if (dataType === 'string') {
      next[key] = String(display);
    }

    this.#commit(next);
  }

  #commit(next: AutoscalingProfileOverridesValue) {
    this.#value.set(next);
    this.#propagateChange(next);
    this.#propagateTouched();
  }
}
